/**
 * Geometry and color constants shared by the sidebar's tree-style connectors.
 *
 * Visual reference (single child of a section):
 *
 *   ::after  →  vertical line, drawn on the leaf/group's bounding box
 *   ::before →  rounded "tick" connecting the line to the item
 *
 *      │            ← line (TREE_LINE.width @ x = -inset)
 *      ╰──── item   ← tick (quarter-arc of radius `curveRadius`,
 *                      then a short straight tail to fill `curveBoxWidth`)
 */
export const TREE_LINE = {
  /** Stroke width of the line and tick. */
  width: '1px',
  /** Radius of the rounded corner where the line meets the tick. */
  curveRadius: 8,
  /** Total horizontal reach of the tick (curve + straight tail). */
  curveBoxWidth: 10,
  /** Distance (px) from the leaf/button left edge back to the vertical line. */
  inset: 12,
  /** Vertical bleed above/below the leaf border-box, matches `my: 0.25` (2 px). */
  segmentBleed: 2,
  /** Half the leaf/group button height (minHeight 36). */
  buttonHalfHeight: 18,
  /** Distance from a group's outer-box top to its button center: `my-top + half-button`. */
  groupButtonCenterFromBoxTop: 20,
} as const;

export type LineMode = 'segment-full' | 'segment-half';

/**
 * Solid (opaque) color used by the line and the tick. Mirrors the Minimal MUI
 * sidebar approach: with an opaque color, two stacked layers (the wrapper/
 * segment line + the tick's curve at the tangent point) paint the exact same
 * pixel value, so the overlap is invisible. Using a semi-transparent color
 * here would composite at the join (alpha 0.14 over 0.14 ≈ 0.26) and produce
 * a visibly thicker join.
 *
 * The literal value approximates `alpha(white, 0.14)` over the sidebar's
 * `grey.800` background.
 */
const TREE_COLOR = '#5A5A5A';

/**
 * SVG path for the tick: a 1px-stroke quarter-arc from the box's top-left
 * (tangent to the wrapper line at x=0.5) down to the bottom and a short
 * horizontal tail. Coords use 0.5 offsets so the 1px stroke lands on full
 * pixel columns/rows.
 *
 * Rendered via mask (instead of `borderBottom` + `borderBottomLeftRadius`)
 * because CSS rounded 1px borders anti-alias differently from a solid 1px
 * `bgcolor` line — the curve looked visibly lighter than the vertical line.
 * Mask + opaque bgcolor lets the curve and the line render with matching
 * visual weight.
 */
const TICK_SVG = encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${TREE_LINE.curveBoxWidth} ${TREE_LINE.curveRadius}' fill='none'>` +
    `<path d='M 0.5 0 a ${TREE_LINE.curveRadius - 0.5} ${TREE_LINE.curveRadius - 0.5} 0 0 0 ${
      TREE_LINE.curveRadius - 0.5
    } ${TREE_LINE.curveRadius - 0.5} H ${TREE_LINE.curveBoxWidth}' ` +
    `stroke='black' stroke-width='1'/></svg>`
);

const TICK_MASK = `url("data:image/svg+xml,${TICK_SVG}")`;

/**
 * `::before` fragment: rounded tick that joins the vertical line to the item.
 * Apply on a `position: relative` button.
 */
export const tickPseudo = () => ({
  content: '""',
  position: 'absolute' as const,
  left: `-${TREE_LINE.inset}px`,
  top: `calc(50% - ${TREE_LINE.curveRadius}px)`,
  width: `${TREE_LINE.curveBoxWidth}px`,
  height: `${TREE_LINE.curveRadius}px`,
  bgcolor: TREE_COLOR,
  maskImage: TICK_MASK,
  WebkitMaskImage: TICK_MASK,
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskSize: '100% 100%',
  WebkitMaskSize: '100% 100%',
});

/**
 * `::after` fragment for a section-level leaf.
 * - `segment-full`: spans the leaf's bounding area (covers `my` bleed on both sides).
 * - `segment-half`: ends at the curve's tangent point (last item in section).
 */
export const leafSegmentPseudo = (mode: LineMode) => ({
  content: '""',
  position: 'absolute' as const,
  left: `-${TREE_LINE.inset}px`,
  top: `-${TREE_LINE.segmentBleed}px`,
  bottom:
    mode === 'segment-half'
      ? `calc(50% + ${TREE_LINE.curveRadius}px)`
      : `-${TREE_LINE.segmentBleed}px`,
  width: TREE_LINE.width,
  bgcolor: TREE_COLOR,
});

/**
 * `::after` fragment for a group's outer Box at section level.
 * The Box has no `my`, so segments meet exactly at adjacent box boundaries.
 */
export const groupSegmentPseudo = (mode: LineMode) => ({
  content: '""',
  position: 'absolute' as const,
  left: `-${TREE_LINE.inset}px`,
  top: 0,
  bottom:
    mode === 'segment-half'
      ? `calc(100% - ${TREE_LINE.groupButtonCenterFromBoxTop - TREE_LINE.curveRadius}px)`
      : 0,
  width: TREE_LINE.width,
  bgcolor: TREE_COLOR,
});

/**
 * `::before` fragment for a group's nested-children wrapper.
 * Continuous line from the wrapper top down to the last leaf's curve tangent point.
 */
export const wrapperLinePseudo = () => ({
  content: '""',
  position: 'absolute' as const,
  left: 0,
  top: 0,
  bottom: `${TREE_LINE.buttonHalfHeight + TREE_LINE.curveRadius}px`,
  width: TREE_LINE.width,
  bgcolor: TREE_COLOR,
});
